/**
 * Checking a supplier's acknowledgement against what we ordered.
 *
 * The acknowledgement or invoice comes back after an order goes in, and the
 * two disagree more often than anyone would like: a quantity short, a price
 * that moved since the catalogue was last updated, a line nobody ordered. Each
 * is cheap to fix on the day and expensive to find three months later in a
 * job's margin.
 *
 * This does the comparison and says nothing about what to do. Whether to argue
 * with the supplier or to accept that the price has genuinely gone up is a
 * judgement, and the person making it needs to see both numbers side by side.
 *
 * Matching is by product where the order line carries one, and by name
 * otherwise. Supplier descriptions are never identical to ours, so name
 * matching is loose — folded to lowercase with punctuation and runs of spaces
 * removed — and a line that matches nothing is reported as unmatched rather
 * than forced onto the nearest guess.
 */

import {
  percentOf,
  subCents,
  toCents,
  toPercent,
  type Cents,
} from "./money.js";

export interface OrderedLine {
  id: string;
  product_id?: string | null;
  name?: string | null;
  description?: string | null;
  qty: unknown;
  unit_price: unknown;
}

/** A line as the supplier printed it. */
export interface SupplierLine {
  description: string;
  qty: number;
  unitPrice: number;
  amount: number;
}

export type DiscrepancyKind =
  "qty" | "price" | "missing_from_supplier" | "not_ordered";

export interface Discrepancy {
  kind: DiscrepancyKind;
  description: string;
  /** The order line, when there is one. */
  itemId: string | null;
  productId: string | null;
  ourQty: number | null;
  theirQty: number | null;
  /** Dollars, as they appear on the documents. */
  ourPrice: number | null;
  theirPrice: number | null;
  /** What the difference is worth on this order, in cents. Signed. */
  costImpact: Cents;
}

/**
 * The totals printed at the foot of the supplier's document.
 *
 * Read off the same paperwork as the lines and just as untrusted: a document
 * that prints no tax line leaves `tax` null, which means "it did not say",
 * never "there was none".
 */
export interface SupplierTotals {
  /** Net of tax, where the document printed it. */
  subtotal: number | null;
  tax: number | null;
  total: number | null;
}

/**
 * The tax on the order against the tax on the supplier's document.
 *
 * Tax is a rate on the order (ADR 0016) and it is typed in, so it is wrong far
 * more often than a price is: the rate is a guess at 9% until a receipt says
 * otherwise, and a delivery across a city line is taxed at the rate where it is
 * delivered. Nothing on the screen used to compare the two, so an order taxed a
 * point light simply understated the job's material cost for ever.
 */
export interface TaxComparison {
  /** Our rate, as a percentage: 9 means 9%. */
  ourRate: number;
  /**
   * Theirs, worked out from their own subtotal and their own tax rather than
   * from ours — a short delivery is taxed on what was actually sent, so the
   * amounts can differ while the rate is identical. Null where the document
   * printed a tax amount but nothing to divide it by.
   */
  theirRate: number | null;
  ourTax: Cents;
  theirTax: Cents;
  /** theirTax - ourTax. Positive means the supplier is charging more. */
  impact: Cents;
  /** Whether the two agree to the cent or two a rounded rate is worth. */
  agrees: boolean;
}

export interface Reconciliation {
  matched: number;
  discrepancies: Discrepancy[];
  /**
   * Sum of every difference, tax included where the document printed it.
   * Positive means the supplier is charging more.
   */
  netImpact: Cents;
  ourTotal: Cents;
  theirTotal: Cents;
  /** Null when the document printed no tax, or when none was asked for. */
  tax: TaxComparison | null;
  /**
   * The supplier lines nothing on the order was matched to, by their position
   * in what was supplied. A position rather than a description: two receipt
   * lines can read exactly alike, and only one of them may be the leftover.
   */
  unplacedSupplied: number[];
}

/** Loose enough to match a supplier's wording, tight enough to mean something. */
export function normaliseDescription(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Words worth comparing.
 *
 * Our own filing prefix goes first — we write "Shingles | GAF Timberline HDZ"
 * and the supplier writes "GAF TIMBERLINE HDZ CHARCOAL", and the category is
 * our word, not the product's. Single characters are dropped as noise.
 */
function tokens(value: string): string[] {
  const withoutCategory = value.includes("|")
    ? value.slice(value.indexOf("|") + 1)
    : value;
  return normaliseDescription(withoutCategory)
    .split(" ")
    .filter((t) => t.length > 1);
}

/**
 * Do these two descriptions name the same thing?
 *
 * Neither side reliably contains the other, so this asks how much of the
 * shorter description turns up in the longer one. Two words in common is the
 * floor: "26 gauge" alone appears on half a metal order, and a wrong match
 * hides a real discrepancy, which is worse than reporting none.
 */
export function describesSameProduct(ours: string, theirs: string): boolean {
  const { shared, share } = wordOverlap(ours, theirs);
  return shared >= 2 && share >= 0.6;
}

/**
 * How many words two descriptions share, and what fraction of the shorter one
 * that is. The measure `describesSameProduct` draws its line on, exported so
 * a shortlist can be ranked by the same words the comparison reads.
 */
export function wordOverlap(
  ours: string,
  theirs: string,
): { shared: number; share: number } {
  const a = tokens(ours);
  const b = tokens(theirs);
  if (a.length === 0 || b.length === 0) return { shared: 0, share: 0 };

  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  const inLonger = new Set(longer);
  const shared = shorter.filter((t) => inLonger.has(t)).length;
  return { shared, share: shared / shorter.length };
}

const asNumber = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/**
 * A cent or two of slack on the tax.
 *
 * A rate is held to two decimal places and tax is charged on a subtotal in the
 * thousands, so two documents at genuinely the same rate land a cent or so
 * apart. Reporting that as a discrepancy would train everybody to ignore the
 * row that matters.
 */
const TAX_TOLERANCE = 2;

/**
 * What the supplier charged in tax, against what the order charges.
 *
 * Their rate is derived rather than read: a receipt prints an amount, and the
 * rate is what that amount is of their own subtotal. Where they printed no
 * subtotal, the lines are added up instead — the same sum the document's own
 * subtotal line would have been.
 *
 * Returns null when the document printed no tax at all. That is "it did not
 * say", and inventing a zero would report every acknowledgement as though the
 * supplier had gone tax-exempt.
 */
export function compareTax(input: {
  /** Sum of our lines, which is what our rate is charged on. */
  ourSubtotal: Cents;
  /** `material_orders.tax_rate`, as a percentage. */
  ourRate: unknown;
  /** Sum of their lines, for a document that printed no subtotal of its own. */
  theirSubtotal: Cents;
  theirs: SupplierTotals;
}): TaxComparison | null {
  const { ourSubtotal, ourRate, theirSubtotal, theirs } = input;
  if (theirs.tax === null) return null;

  const rate = toPercent(ourRate);
  const ourTax = percentOf(ourSubtotal, rate);
  const theirTax = toCents(theirs.tax);

  // Their own subtotal first: it is what they charged the tax on, and it is
  // the only figure that gives the right rate when a line was read wrong.
  const base =
    theirs.subtotal !== null && theirs.subtotal > 0
      ? toCents(theirs.subtotal)
      : theirSubtotal;
  const theirRate =
    base > 0 ? Math.round((theirTax / base) * 10000) / 100 : null;

  const impact = subCents(theirTax, ourTax);
  return {
    ourRate: rate,
    theirRate,
    ourTax,
    theirTax,
    impact,
    agrees: Math.abs(impact) <= TAX_TOLERANCE,
  };
}

/**
 * Compare an order with what the supplier says it will send.
 *
 * `productMatch` maps a supplier's description onto one of our product ids
 * where the caller can do better than string matching — a previous
 * reconciliation, say. It is optional and everything works without it.
 *
 * `taxes` is the order's own rate and the totals printed on their document.
 * Optional too: a caller that has not read the foot of the page gets the same
 * line-by-line comparison it has always had, and `tax` comes back null.
 */
export function reconcileOrder(
  ordered: readonly OrderedLine[],
  supplied: readonly SupplierLine[],
  productMatch?: (description: string) => string | null,
  taxes?: { ourRate: unknown; theirs: SupplierTotals },
  /**
   * Which order line a supplier line is, by the supplier line's position in
   * `supplied`, where something better than word overlap has said so: Jev, in
   * `receiptLineMatches` (ADR 0155). By position, not description, because two
   * receipt lines can share a description and an answer about one must not be
   * applied to the other. It decides before word overlap does, and a supplier
   * line it gave to one order line is never taken by another. Optional;
   * without it nothing changes.
   */
  lineMatch?: (suppliedIndex: number) => string | null,
): Reconciliation {
  const remaining = supplied.map((line, index) => ({ line, index }));
  const used = new Set<number>();
  const discrepancies: Discrepancy[] = [];
  let matched = 0;

  const findSupplied = (line: OrderedLine) => {
    const ours = line.name ?? line.description ?? "";
    const wantedId = line.product_id ?? null;

    if (lineMatch) {
      const named = remaining.find(
        (c) => !used.has(c.index) && lineMatch(c.index) === line.id,
      );
      if (named) return named;
    }

    for (const candidate of remaining) {
      if (used.has(candidate.index)) continue;
      const theirs = candidate.line.description;
      const claimed = lineMatch?.(candidate.index) ?? null;
      if (claimed !== null && claimed !== line.id) continue;

      const byProduct =
        wantedId !== null &&
        productMatch?.(candidate.line.description) === wantedId;
      if (byProduct || describesSameProduct(ours, theirs)) return candidate;
    }
    return null;
  };

  for (const line of ordered) {
    const ourQty = asNumber(line.qty);
    const ourPrice = asNumber(line.unit_price);
    const description = line.name ?? line.description ?? "(unnamed line)";

    const hit = findSupplied(line);
    if (!hit) {
      discrepancies.push({
        kind: "missing_from_supplier",
        description,
        itemId: line.id,
        productId: line.product_id ?? null,
        ourQty,
        theirQty: null,
        ourPrice,
        theirPrice: null,
        // Nothing is being charged for it, so the order is short, not dearer.
        costImpact: toCents(-(ourQty * ourPrice)),
      });
      continue;
    }

    used.add(hit.index);
    matched += 1;

    const theirQty = hit.line.qty;
    const theirPrice = hit.line.unitPrice;

    if (Math.abs(theirQty - ourQty) > 0.001) {
      discrepancies.push({
        kind: "qty",
        description,
        itemId: line.id,
        productId: line.product_id ?? null,
        ourQty,
        theirQty,
        ourPrice,
        theirPrice,
        costImpact: toCents((theirQty - ourQty) * ourPrice),
      });
    }

    if (Math.abs(theirPrice - ourPrice) > 0.005) {
      discrepancies.push({
        kind: "price",
        description,
        itemId: line.id,
        productId: line.product_id ?? null,
        ourQty,
        theirQty,
        ourPrice,
        theirPrice,
        // Priced on what they say they are sending.
        costImpact: toCents((theirPrice - ourPrice) * theirQty),
      });
    }
  }

  const unplacedSupplied: number[] = [];
  for (const candidate of remaining) {
    if (used.has(candidate.index)) continue;
    const { line } = candidate;
    unplacedSupplied.push(candidate.index);
    discrepancies.push({
      kind: "not_ordered",
      description: line.description,
      itemId: null,
      productId: null,
      ourQty: null,
      theirQty: line.qty,
      ourPrice: null,
      theirPrice: line.unitPrice,
      costImpact: toCents(line.amount),
    });
  }

  const ourTotal = toCents(
    ordered.reduce(
      (sum, l) => sum + asNumber(l.qty) * asNumber(l.unit_price),
      0,
    ),
  );
  const theirTotal = toCents(supplied.reduce((sum, l) => sum + l.amount, 0));

  const tax = taxes
    ? compareTax({
        ourSubtotal: ourTotal,
        ourRate: taxes.ourRate,
        theirSubtotal: theirTotal,
        theirs: taxes.theirs,
      })
    : null;

  const lineImpact = discrepancies.reduce(
    (sum, d) => sum + d.costImpact / 100,
    0,
  );

  return {
    matched,
    discrepancies,
    // Tax is part of what this order costs, so a rate that disagrees belongs in
    // the one number somebody reads off this screen. A tax difference the lines
    // caused is already in it — that is the same money, counted once.
    netImpact: toCents(lineImpact + (tax ? tax.impact / 100 : 0)),
    ourTotal,
    theirTotal,
    tax,
    unplacedSupplied,
  };
}
